import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";

import {
  AIMethodologyWarning,
  AISkippedCategory,
  DraftValidation,
} from "./types";

const MODEL_WARNING_LABEL_KEYS: Record<string, string> = {
  new_hazard_introduced: "risks.ai.warnings.newHazard",
};

interface WarningsPanelProps {
  validation: DraftValidation;
  generationWarnings: AIMethodologyWarning[];
  skippedCategories: AISkippedCategory[];
  defaultOpen?: boolean;
}

export const hasAcknowledgeableWarnings = (
  validation: DraftValidation,
  generationWarnings: AIMethodologyWarning[],
  skippedCategories: AISkippedCategory[]
): boolean =>
  validation.warnings.length > 0 ||
  (generationWarnings?.length ?? 0) > 0 ||
  (skippedCategories?.length ?? 0) > 0;

export const WarningsPanel = ({
  validation,
  generationWarnings,
  skippedCategories,
  defaultOpen = true,
}: WarningsPanelProps) => {
  const { t } = useTranslation();
  const [isErrorsOpen, setIsErrorsOpen] = useState(defaultOpen);
  const [isWarningsOpen, setIsWarningsOpen] = useState(defaultOpen);

  const showErrors = validation.errors.length > 0;
  const showWarnings = hasAcknowledgeableWarnings(
    validation,
    generationWarnings,
    skippedCategories
  );

  if (!showErrors && !showWarnings) {
    return null;
  }

  return (
    <div className="space-y-3">
      {showErrors && (
        <div
          data-test="ai-draft-errors"
          className="rounded-lg border border-red-300 bg-red-50 overflow-hidden"
        >
          <button
            type="button"
            onClick={() => setIsErrorsOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 text-left focus:outline-none"
          >
            <span className="text-sm font-semibold text-red-800">
              {t("risks.ai.invalid.headline")}
            </span>
            {isErrorsOpen ? (
              <ChevronUpIcon className="h-4 w-4 text-red-400 flex-shrink-0" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-red-400 flex-shrink-0" aria-hidden="true" />
            )}
          </button>
          {isErrorsOpen && (
            <ul className="px-4 pb-4 border-t border-red-200 mt-0 list-disc list-inside space-y-1 pt-3">
              {validation.errors.map((issue, index) => (
                <li
                  key={`${issue.code}-${issue.section}-${index}`}
                  className="text-sm text-red-700"
                >
                  {t(issue.i18nKey, issue.params)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showWarnings && (
        <div
          data-test="ai-draft-warnings"
          className="rounded-lg border border-yellow-300 bg-yellow-50 overflow-hidden"
        >
          <button
            type="button"
            onClick={() => setIsWarningsOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-4 py-3 text-left focus:outline-none"
          >
            <span className="text-sm font-semibold text-yellow-800">
              {t("risks.ai.warnings.headline")}
            </span>
            {isWarningsOpen ? (
              <ChevronUpIcon className="h-4 w-4 text-yellow-500 flex-shrink-0" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-yellow-500 flex-shrink-0" aria-hidden="true" />
            )}
          </button>
          {isWarningsOpen && (
            <ul className="px-4 pb-4 border-t border-yellow-200 list-disc list-inside space-y-1 pt-3">
              {validation.warnings.map((issue, index) => (
                <li
                  key={`${issue.code}-${issue.section}-${index}`}
                  className="text-sm text-yellow-700"
                >
                  {t(issue.i18nKey, issue.params)}
                </li>
              ))}
              {(generationWarnings ?? []).map((warning, index) => (
                <li key={`model-${index}`} className="text-sm text-yellow-700">
                  {MODEL_WARNING_LABEL_KEYS[warning.code] && (
                    <span className="font-medium">
                      {t(MODEL_WARNING_LABEL_KEYS[warning.code])}
                      {": "}
                    </span>
                  )}
                  {warning.message}
                </li>
              ))}
              {(skippedCategories ?? []).map((skipped, index) => (
                <li key={`skipped-${index}`} className="text-sm text-yellow-700">
                  <span className="font-medium">
                    {t("risks.ai.warnings.skipped", {
                      category: skipped.category,
                    })}
                    {": "}
                  </span>
                  {skipped.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
